import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import { Helpers } from "../../../helpers/Helpers";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import { storeActivityIndicator, storeAreYouSureModalCallback } from "../../../redux/reducers/authSlice";
import { RootState } from "../../../redux/store";
import {
    HGS_KURUMSAL_SAYFASI, HgsAktarimHatasi, HgsAktarimOzeti, HgsBakiyeHareketi, HgsHesabi, HgsKurumsalDurum, HgsKurumsalService
} from "../../../services/HgsKurumsalService";
import {
    aktarimRengi, EklentiCevabi, eklentiSurumu, eklentiyeSor, HGS_ADRESI, hesapNoGecerli, oturumDurumuAdi, oturumRengi, para, saatGecerli, tarih, tarihSaat
} from "../../../helpers/hgsEklenti";

type Form = { id?: string, hesapNo: string, hesapAdi: string, aktif: boolean, otomatikSenkron: boolean, cekimSaati: string };
const bosForm: Form = { hesapNo: '', hesapAdi: '', aktif: true, otomatikSenkron: false, cekimSaati: '03:00' };
type Sekme = 'aktarimlar' | 'hatalar' | 'bakiye';

/**
 * Ayarlar > Entegrasyon Ayarları > HGS Kurumsal (04.11.2026, Burak; talimat docs/hgs-kurumsal-gelistirme-talimati.md).
 * A1 Açılış: eklenti durumu (kurulu / bağlı / ortam), HGS oturum durumu, hesaplar, son aktarımlar, bekleyen hata sayısı.
 * R1 GIVEN eklenti kurulu WHEN "Eklentiyi bağla" THEN sunucu 12 saatlik anahtar verir, eklentiye iletilir (ekranda gösterilmez).
 * R2 WHEN "HGS'ye Giriş Yap" THEN HGS Kurumsal yeni sekmede açılır; giriş ve reCAPTCHA kullanıcıdadır.
 * R3 GIVEN HGS oturumu açık WHEN "HGS Çek" THEN eklenti ekrandaki tabloları okur, Keylogx'e aktarır; sonuç sayıları gösterilir.
 * R4 WHEN "Bağlantıyı kes" THEN firmanın bütün eklenti anahtarları iptal edilir, eklentideki anahtar silinir.
 * HGS kullanıcı adı / şifresi için alan yoktur.
 */
const HgsKurumsal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector((state: RootState) => state.auth);
    const guncelleyebilir = !!auth.user.user?.data.authorizedMenus.find(x => x.menuName == HGS_KURUMSAL_SAYFASI)?.canUpdate;

    const [durum, setDurum] = useState<HgsKurumsalDurum | null>(null);
    const [eklenti, setEklenti] = useState<EklentiCevabi | null>(null);
    const [form, setForm] = useState<Form | null>(null);
    const [cekHesap, setCekHesap] = useState('');
    const [cekSonucu, setCekSonucu] = useState<any>(null);
    const [sekme, setSekme] = useState<Sekme>('aktarimlar');
    const [aktarimlar, setAktarimlar] = useState<HgsAktarimOzeti[]>([]);
    const [hatalar, setHatalar] = useState<HgsAktarimHatasi[]>([]);
    const [hataDurumu, setHataDurumu] = useState(1);
    const [secili, setSecili] = useState<string[]>([]);
    const [bakiyeler, setBakiyeler] = useState<HgsBakiyeHareketi[]>([]);
    const [bakiyeFiltre, setBakiyeFiltre] = useState({ hesapId: '', bas: '', bit: '' });

    useEffect(() => { yenile(); }, []);
    useEffect(() => { sekmeyiYukle(); }, [sekme, hataDurumu]);

    const calistir = async <T,>(is_: () => Promise<any>, basarili?: (data: T) => void) => {
        try {
            dispatch(storeActivityIndicator(true));
            const r = await is_();
            if (!r.isSuccess) { toast.error(r.message ?? ''); return false; }
            basarili?.(r.data);
            return true;
        } catch (error: any) {
            toast.error(error);
            return false;
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const eklentiDurumunuAl = async () => setEklenti(await eklentiyeSor('hgsEklentiDurum', undefined, 2500));

    const yenile = async () => {
        await calistir<HgsKurumsalDurum>(() => HgsKurumsalService.durum(), d => {
            setDurum(d);
            const aktifler = d.hesaplar.filter(x => x.aktif);
            if (aktifler.length === 1) setCekHesap(aktifler[0].hesapNo);
        });
        eklentiDurumunuAl();
        sekmeyiYukle();
    }

    const sekmeyiYukle = async () => {
        if (sekme === 'aktarimlar') await calistir<HgsAktarimOzeti[]>(() => HgsKurumsalService.aktarimlar(), setAktarimlar);
        if (sekme === 'hatalar') { setSecili([]); await calistir<HgsAktarimHatasi[]>(() => HgsKurumsalService.hatalar(hataDurumu), setHatalar); }
        if (sekme === 'bakiye') await calistir<HgsBakiyeHareketi[]>(() => HgsKurumsalService.bakiyeHareketleri({
            hgsHesabiId: bakiyeFiltre.hesapId || undefined, baslangic: bakiyeFiltre.bas || undefined, bitis: bakiyeFiltre.bit || undefined
        }), setBakiyeler);
    }

    // ---------------------------------------------------------------- eklenti
    const eklentiyiBagla = async () => {
        if (!eklentiSurumu()) { toast.error('Keylogx HGS Aktarım eklentisi bu tarayıcıda kurulu değil.'); return; }
        let anahtar: { anahtar: string, bitis: string } | null = null;
        const ok = await calistir<any>(() => HgsKurumsalService.eklentiBagla(), d => { anahtar = d; });
        if (!ok || !anahtar) return;
        const c = await eklentiyeSor('hgsEklentiBagla', anahtar, 8000);
        if (!c.ok) { toast.error(c.hata ?? 'Eklenti bağlanamadı.'); return; }
        toast.success(`Eklenti ${c.firmaAdi ?? ''} firmasına bağlandı.`);
        yenile();
    }

    const hgsyeGiris = () => window.open(HGS_ADRESI, '_blank', 'noopener');

    const hgsCek = async () => {
        const aktifler = durum?.hesaplar.filter(x => x.aktif) ?? [];
        if (aktifler.length > 1 && !cekHesap) { toast.error('Aktarılacak HGS hesabını seçin.'); return; }
        setCekSonucu(null);
        try {
            dispatch(storeActivityIndicator(true));
            const c = await eklentiyeSor('hgsCek', { hesapNo: cekHesap || null }, 15 * 60 * 1000);
            if (!c.ok) { toast.error(c.hata ?? 'HGS çekimi başarısız.'); return; }
            setCekSonucu(c);
            if (c.sonuc?.durum === 1) toast.success('HGS kayıtları aktarıldı.'); else toast(c.sonuc?.durumAdi ?? '');
        } finally {
            dispatch(storeActivityIndicator(false));
        }
        yenile();
    }

    const baglantiyiKes = () => {
        dispatch(storeAreYouSureModalCallback(async () => {
            const ok = await calistir<number>(() => HgsKurumsalService.baglantiKes(), n => toast.success(`${n} eklenti bağlantısı kesildi.`));
            if (ok) await eklentiyeSor('hgsBaglantiKes', undefined, 2500);
            yenile();
        }));
    }

    // ---------------------------------------------------------------- hesaplar
    const kaydet = async () => {
        if (!form) return;
        if (!hesapNoGecerli(form.hesapNo)) { toast.error('HGS hesap numarası 4–30 harf/rakam olmalı.'); return; }
        if (!saatGecerli(form.cekimSaati)) { toast.error('Günlük çekim saatini ss:dd biçiminde girin (ör. 03:00).'); return; }
        const ok = await calistir(() => HgsKurumsalService.hesapKaydet({
            id: form.id, hesapNo: form.hesapNo.trim(), hesapAdi: form.hesapAdi.trim() || undefined,
            aktif: form.aktif, otomatikSenkron: form.otomatikSenkron, cekimSaati: form.cekimSaati.trim()
        }));
        if (ok) { toast.success('HGS hesabı kaydedildi.'); setForm(null); yenile(); }
    }

    const sil = (h: HgsHesabi) => dispatch(storeAreYouSureModalCallback(async () => {
        if (await calistir(() => HgsKurumsalService.hesapSil([h.id]))) { toast.success('HGS hesabı silindi.'); yenile(); }
    }));

    // ---------------------------------------------------------------- hatalar
    const hataIslem = async (tur: 'yenidenIsle' | 'yokSay') => {
        if (!secili.length) { toast.error('Önce satır seçin.'); return; }
        if (tur === 'yenidenIsle')
            await calistir<any>(() => HgsKurumsalService.hataYenidenIsle(secili), s => toast.success(`${s.duzeltilen} kayıt aktarıldı, ${s.kalan} kayıt hâlâ hatalı.`));
        else
            await calistir<number>(() => HgsKurumsalService.hataYokSay(secili), n => toast.success(`${n} kayıt yok sayıldı.`));
        yenile();
    }

    // ---------------------------------------------------------------- görünüm
    const surum = eklentiSurumu();
    const eklentiDurumu = () => {
        if (!surum) return <span className="badge badge-light-danger">Kurulu değil</span>;
        if (!eklenti) return <span className="badge badge-light">Kontrol ediliyor…</span>;
        if (!eklenti.ok) return <span className="badge badge-light-danger">{eklenti.hata}</span>;
        if (eklenti.bagli) return <span className="badge badge-light-success">Bağlı · {tarihSaat(eklenti.bitis)}'e kadar</span>;
        if (eklenti.baskaOrtam) return <span className="badge badge-light-warning">{eklenti.baskaOrtam} ortamına bağlı</span>;
        if (eklenti.suresiDoldu) return <span className="badge badge-light-warning">Süresi doldu</span>;
        return <span className="badge badge-light-warning">Bağlı değil</span>;
    }
    const hgsOturum = eklenti?.ok ? eklenti.hgs?.durum : durum?.hesaplar.find(x => x.sonOturumDurumu)?.sonOturumDurumu;
    const aktifHesaplar = durum?.hesaplar.filter(x => x.aktif) ?? [];
    const cekilebilir = !!eklenti?.ok && !!eklenti.bagli && eklenti.hgs?.durum === 'GIRIS_YAPILDI' && aktifHesaplar.length > 0;
    const sonuc = cekSonucu?.sonuc;

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{ title: 'Ayarlar > Entegrasyon Ayarları > ', isActive: false, path: '/integrationsetting/list' }]}>HGS Kurumsal</PageTitle>

            <Box className="mb-10">
                <Content>
                    {/* ---------------- bağlantı */}
                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <h4 className="mb-6 text-black">Bağlantı</h4>
                            <div className="row g-6">
                                <div className="col-md-3">
                                    <div className="text-muted fs-7 mb-1">Eklenti</div>
                                    <div id="hgs-eklenti-durumu">{eklentiDurumu()}</div>
                                    <div className="text-muted fs-8 mt-1">{surum ? `Sürüm ${surum}` : 'Chrome / Edge eklentisini kurun'}</div>
                                </div>
                                <div className="col-md-3">
                                    <div className="text-muted fs-7 mb-1">HGS oturumu</div>
                                    <span id="hgs-oturum-durumu" className={`badge badge-light-${oturumRengi(hgsOturum)}`}>{oturumDurumuAdi(hgsOturum)}</span>
                                </div>
                                <div className="col-md-3">
                                    <div className="text-muted fs-7 mb-1">Son başarılı aktarım</div>
                                    <div className="fw-semibold">{tarihSaat(aktifHesaplar.map(x => x.sonBasariliAktarim).filter(Boolean).sort().reverse()[0]) || '—'}</div>
                                </div>
                                <div className="col-md-3">
                                    <div className="text-muted fs-7 mb-1">Bekleyen aktarım hatası</div>
                                    <div className={'fw-bold ' + ((durum?.bekleyenHata ?? 0) > 0 ? 'text-danger' : '')}>{durum?.bekleyenHata ?? 0}</div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-3 align-items-end mt-8">
                                <button id="hgs-giris" className="btn btn-light-primary btn-sm" onClick={hgsyeGiris}>HGS'ye Giriş Yap</button>
                                {aktifHesaplar.length > 1 && (
                                    <div>
                                        <label className="form-label fs-8 mb-1" htmlFor="hgs-cek-hesap">Aktarılacak hesap</label>
                                        <select id="hgs-cek-hesap" className="form-select form-select-sm" value={cekHesap} onChange={e => setCekHesap(e.target.value)}>
                                            <option value="">Seçin</option>
                                            {aktifHesaplar.map(h => <option key={h.id} value={h.hesapNo}>{h.hesapNo}{h.hesapAdi ? ` · ${h.hesapAdi}` : ''}</option>)}
                                        </select>
                                    </div>
                                )}
                                <button id="hgs-cek" className="btn btn-primary btn-sm" onClick={hgsCek} disabled={!cekilebilir}
                                    title={cekilebilir ? '' : 'Eklenti bağlı ve HGS oturumu açık olmalı; en az bir aktif hesap gerekir.'}>HGS Çek</button>
                                <button id="hgs-bagla" className="btn btn-light btn-sm" onClick={eklentiyiBagla} disabled={!surum}>
                                    {eklenti?.bagli ? 'Bağlantıyı yenile' : 'Eklentiyi bağla'}
                                </button>
                                <button id="hgs-kes" className="btn btn-light-danger btn-sm" onClick={baglantiyiKes} disabled={!durum?.aktifBaglanti && !eklenti?.bagli}>Bağlantıyı Kes</button>
                                <button id="hgs-yenile" className="btn btn-light btn-sm" onClick={yenile}>Yenile</button>
                            </div>

                            {sonuc && (
                                <div id="hgs-cek-sonucu" className={`p-4 rounded bg-light-${aktarimRengi(sonuc.durum)} fs-7 mt-6`}>
                                    <b>{sonuc.durumAdi}{sonuc.tekrarlandi ? ' (daha önce işlenmiş)' : ''} · hesap {cekSonucu.hesapNo}</b>
                                    <div>{sonuc.mesaj}</div>
                                    {[...(cekSonucu.uyarilar ?? []), ...(sonuc.hatalar ?? [])].length > 0 && (
                                        <ul className="mb-0 mt-2">{[...(cekSonucu.uyarilar ?? []), ...(sonuc.hatalar ?? []).slice(0, 10)].map((x: string, i: number) => <li key={i}>{x}</li>)}</ul>
                                    )}
                                </div>
                            )}

                            <div className="text-muted fs-8 mt-6">
                                HGS'ye giriş ve Google doğrulaması (reCAPTCHA) sizin tarayıcınızda, sizin tarafınızdan yapılır. HGS kullanıcı adı, şifresi ve oturum çerezi Keylogx'e gönderilmez.
                                HGS Çek için HGS'de bir aracın GEÇİŞLER ya da BAKİYE HAREKETLERİ penceresini açık bırakın; eklenti o penceredeki tabloları portalın sayfa düğmesiyle sırayla okur (sayfa başına 2–3,5 sn). İlk çekimde 01.08.2026 sonrası, sonraki çekimlerde aracın son çekiminden 3 gün öncesine kadar okunur; daha eski sayfalara geçilmez.
                                Eklenti bağlantısı {durum?.anahtarSaati ?? 12} saat geçerlidir.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    {/* ---------------- hesaplar */}
                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <div className="d-flex justify-content-between align-items-center mb-6">
                                <h4 className="mb-0 text-black">HGS hesapları</h4>
                                {guncelleyebilir && !form && <button id="hgs-hesap-ekle" className="btn btn-primary btn-sm" onClick={() => setForm({ ...bosForm })}>Hesap ekle</button>}
                            </div>

                            {form && (
                                <div className="border rounded p-6 mb-6">
                                    <div className="row g-4">
                                        <div className="col-md-3">
                                            <label className="form-label required" htmlFor="hgs-form-no">HGS hesap no</label>
                                            <input id="hgs-form-no" className="form-control form-control-sm" value={form.hesapNo} maxLength={40} onChange={e => setForm({ ...form, hesapNo: e.target.value })} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label" htmlFor="hgs-form-ad">Hesap adı</label>
                                            <input id="hgs-form-ad" className="form-control form-control-sm" value={form.hesapAdi} maxLength={100} onChange={e => setForm({ ...form, hesapAdi: e.target.value })} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label" htmlFor="hgs-form-saat">Günlük çekim saati</label>
                                            <input id="hgs-form-saat" type="time" className="form-control form-control-sm" value={form.cekimSaati} onChange={e => setForm({ ...form, cekimSaati: e.target.value })} />
                                        </div>
                                        <div className="col-md-4 d-flex flex-column justify-content-end gap-2">
                                            <label className="form-check form-check-sm form-check-custom">
                                                <input id="hgs-form-aktif" className="form-check-input" type="checkbox" checked={form.aktif} onChange={e => setForm({ ...form, aktif: e.target.checked })} />
                                                <span className="form-check-label">Entegrasyon aktif</span>
                                            </label>
                                            <label className="form-check form-check-sm form-check-custom">
                                                <input id="hgs-form-otomatik" className="form-check-input" type="checkbox" checked={form.otomatikSenkron} onChange={e => setForm({ ...form, otomatikSenkron: e.target.checked })} />
                                                <span className="form-check-label">Otomatik günlük çekim</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="text-muted fs-8 mt-4">
                                        Hesap firmanıza bağlanır; aynı HGS hesabı iki firmaya bağlanamaz. Otomatik çekim tarayıcı açık ve HGS oturumu geçerliyken çalışır;
                                        HGS kullanım izni netleşene kadar eklenti seçilen saatte yalnız hatırlatır.
                                    </div>
                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button className="btn btn-light btn-sm" onClick={() => setForm(null)}>Vazgeç</button>
                                        <button id="hgs-form-kaydet" className="btn btn-primary btn-sm" onClick={kaydet}>Kaydet</button>
                                    </div>
                                </div>
                            )}

                            <div className="table-responsive">
                                <table className="table align-middle table-row-dashed fs-7 gy-3 ebbe-compact-table" id="hgs-hesaplar">
                                    <thead><tr className="text-muted fw-bold text-uppercase fs-8">
                                        <th>Hesap no</th><th>Hesap adı</th><th>Entegrasyon</th><th>Otomatik çekim</th><th>HGS oturumu</th>
                                        <th>Son başarılı aktarım</th><th>Son aktarım sonucu</th><th></th>
                                    </tr></thead>
                                    <tbody>
                                        {(durum?.hesaplar ?? []).length === 0 && <tr><td colSpan={8} className="text-center text-muted py-6">Henüz HGS hesabı yok. "Hesap ekle" ile firmanıza bağlayın.</td></tr>}
                                        {(durum?.hesaplar ?? []).map(h => (
                                            <tr key={h.id}>
                                                <td className="fw-semibold">{h.hesapNo}</td>
                                                <td>{h.hesapAdi}</td>
                                                <td><span className={`badge badge-light-${h.aktif ? 'success' : 'secondary'}`}>{h.aktif ? 'Aktif' : 'Pasif'}</span></td>
                                                <td>{h.otomatikSenkron ? `Açık · ${h.cekimSaati}` : 'Kapalı'}</td>
                                                <td>{h.sonOturumDurumu ? <span title={tarihSaat(h.sonOturumZamani)}>{oturumDurumuAdi(h.sonOturumDurumu)}</span> : '—'}</td>
                                                <td>{tarihSaat(h.sonBasariliAktarim) || '—'}</td>
                                                <td style={{ maxWidth: 360 }}>
                                                    <div className="text-truncate" title={h.sonAktarimSonucu ?? ''}>{h.sonAktarimSonucu ?? '—'}</div>
                                                    {h.sonHata && <div className="text-danger text-truncate" title={h.sonHata}>{h.sonHata}</div>}
                                                </td>
                                                <td className="text-end text-nowrap">
                                                    {guncelleyebilir && <>
                                                        <button className="btn btn-sm btn-light btn-active-light-primary me-2"
                                                            onClick={() => setForm({ id: h.id, hesapNo: h.hesapNo, hesapAdi: h.hesapAdi ?? '', aktif: h.aktif, otomatikSenkron: h.otomatikSenkron, cekimSaati: h.cekimSaati })}>Düzenle</button>
                                                        <button className="btn btn-sm btn-light-danger" onClick={() => sil(h)}>Sil</button>
                                                    </>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    {/* ---------------- aktarımlar / hatalar / bakiye */}
                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <ul className="nav nav-tabs nav-line-tabs mb-6 fs-6">
                                {([['aktarimlar', 'Son aktarımlar'], ['hatalar', `Aktarım hataları${durum?.bekleyenHata ? ` (${durum.bekleyenHata})` : ''}`], ['bakiye', 'Bakiye hareketleri']] as [Sekme, string][]).map(([k, ad]) => (
                                    <li className="nav-item" key={k}>
                                        <a href="#" id={`hgs-sekme-${k}`} className={'nav-link' + (sekme === k ? ' active' : '')} onClick={e => { e.preventDefault(); setSekme(k); }}>{ad}</a>
                                    </li>
                                ))}
                            </ul>

                            {sekme === 'aktarimlar' && (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-7 gy-3 ebbe-compact-table" id="hgs-aktarimlar">
                                        <thead><tr className="text-muted fw-bold text-uppercase fs-8">
                                            <th>Tarih</th><th>Plaka</th><th>Hesap</th><th>Kullanıcı</th><th>Sorgu aralığı</th><th>Durum</th>
                                            <th className="text-end">Okunan</th><th className="text-end">Yeni</th><th className="text-end">Güncellenen</th><th className="text-end">Tekrar</th>
                                            <th className="text-end">Eşleşmeyen plaka</th><th className="text-end">Hatalı</th><th className="text-end text-nowrap">Bakiye (yeni)</th><th className="ps-6">Hata</th>
                                        </tr></thead>
                                        <tbody>
                                            {aktarimlar.length === 0 && <tr><td colSpan={14} className="text-center text-muted py-6">Henüz aktarım yok.</td></tr>}
                                            {aktarimlar.map(a => (
                                                <tr key={a.id}>
                                                    <td className="text-nowrap">{tarihSaat(a.tarih)}</td>
                                                    <td className="fw-semibold" style={{ maxWidth: 220 }}><div className="text-truncate" title={a.plakalar ?? ''}>{a.plakalar || '—'}</div></td>
                                                    <td>{a.hesapNo}</td>
                                                    <td>{a.kullanici}</td>
                                                    <td className="text-nowrap">{a.sorguBaslangic ? `${tarih(a.sorguBaslangic)} – ${tarih(a.sorguBitis)}` : '—'}</td>
                                                    <td><span className={`badge badge-light-${aktarimRengi(a.durum)}`}>{a.durumAdi}</span></td>
                                                    <td className="text-end">{a.okunan}</td><td className="text-end">{a.yeni}</td><td className="text-end">{a.guncellenen}</td><td className="text-end">{a.tekrar}</td>
                                                    <td className="text-end">{a.eslesmeyenPlaka}</td><td className="text-end">{a.hatali}</td>
                                                    <td className="text-end">{a.bakiyeOkunan} ({a.bakiyeYeni})</td>
                                                    <td className="text-danger" style={{ maxWidth: 280 }}><div className="text-truncate" title={a.hata ?? ''}>{a.hata}</div></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {sekme === 'hatalar' && (
                                <>
                                    <div className="d-flex flex-wrap gap-3 align-items-end mb-4">
                                        <div>
                                            <label className="form-label fs-8 mb-1" htmlFor="hgs-hata-durum">Durum</label>
                                            <select id="hgs-hata-durum" className="form-select form-select-sm" value={hataDurumu} onChange={e => setHataDurumu(Number(e.target.value))}>
                                                <option value={1}>Bekliyor</option><option value={2}>Düzeltildi</option><option value={3}>Yok sayıldı</option>
                                            </select>
                                        </div>
                                        {hataDurumu === 1 && <>
                                            <button id="hgs-yeniden-isle" className="btn btn-light-primary btn-sm" onClick={() => hataIslem('yenidenIsle')} disabled={!secili.length}>Yeniden işle</button>
                                            <button id="hgs-yok-say" className="btn btn-light btn-sm" onClick={() => hataIslem('yokSay')} disabled={!secili.length}>Yok say</button>
                                        </>}
                                        <span className="text-muted fs-8">Plakası eşleşmeyen geçiş için araç kartındaki plakayı düzeltip "Yeniden işle"ye basın; bir sonraki HGS Çek'te de kendiliğinden düzelir.</span>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-7 gy-3 ebbe-compact-table" id="hgs-hatalar">
                                            <thead><tr className="text-muted fw-bold text-uppercase fs-8">
                                                {hataDurumu === 1 && <th style={{ width: 30 }}>
                                                    <input className="form-check-input" type="checkbox" aria-label="Tümünü seç" checked={hatalar.length > 0 && secili.length === hatalar.length}
                                                        onChange={e => setSecili(e.target.checked ? hatalar.map(x => x.id) : [])} />
                                                </th>}
                                                <th>Geçiş</th><th>Tür</th><th>Hesap</th><th>Plaka</th><th>Giriş</th><th>Çıkış</th><th className="text-end">Ücret</th><th>Referans</th><th>Hata</th><th>Durum</th>
                                            </tr></thead>
                                            <tbody>
                                                {hatalar.length === 0 && <tr><td colSpan={11} className="text-center text-muted py-6">Kayıt yok.</td></tr>}
                                                {hatalar.map(h => (
                                                    <tr key={h.id}>
                                                        {hataDurumu === 1 && <td><input className="form-check-input" type="checkbox" aria-label="Seç" checked={secili.includes(h.id)}
                                                            onChange={e => setSecili(e.target.checked ? [...secili, h.id] : secili.filter(x => x !== h.id))} /></td>}
                                                        <td className="text-nowrap">{tarih(h.gecisTarihi)} {h.gecisSaati}</td>
                                                        <td>{h.kaynakTuru}</td><td>{h.hesapNo}</td><td>{h.plaka}</td><td>{h.girisYeri}</td><td>{h.cikisYeri}</td>
                                                        <td className="text-end">{para(h.ucret)}</td><td>{h.hariciIslemNo}</td>
                                                        <td className="text-danger">{h.hata}</td><td>{h.durumAdi}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {sekme === 'bakiye' && (
                                <>
                                    <div className="d-flex flex-wrap gap-3 align-items-end mb-4">
                                        <div>
                                            <label className="form-label fs-8 mb-1" htmlFor="hgs-bakiye-hesap">Hesap</label>
                                            <select id="hgs-bakiye-hesap" className="form-select form-select-sm" value={bakiyeFiltre.hesapId} onChange={e => setBakiyeFiltre({ ...bakiyeFiltre, hesapId: e.target.value })}>
                                                <option value="">Tümü</option>
                                                {(durum?.hesaplar ?? []).map(h => <option key={h.id} value={h.id}>{h.hesapNo}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="form-label fs-8 mb-1" htmlFor="hgs-bakiye-bas">Başlangıç</label>
                                            <input id="hgs-bakiye-bas" type="date" className="form-control form-control-sm" value={bakiyeFiltre.bas} onChange={e => setBakiyeFiltre({ ...bakiyeFiltre, bas: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="form-label fs-8 mb-1" htmlFor="hgs-bakiye-bit">Bitiş</label>
                                            <input id="hgs-bakiye-bit" type="date" className="form-control form-control-sm" value={bakiyeFiltre.bit} onChange={e => setBakiyeFiltre({ ...bakiyeFiltre, bit: e.target.value })} />
                                        </div>
                                        <button className="btn btn-light-primary btn-sm" onClick={sekmeyiYukle}>Listele</button>
                                        <span className="text-muted fs-8">Bakiye hareketleri gider değildir; sefer maliyetine girmez. Geçiş ücreti düşümleri işaretlidir, gider HGS Kayıtları'nda bir kez yer alır.</span>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-7 gy-3 ebbe-compact-table" id="hgs-bakiye">
                                            <thead><tr className="text-muted fw-bold text-uppercase fs-8">
                                                <th>İşlem zamanı</th><th>Hesap</th><th>Plaka</th><th>Tür</th><th>Açıklama</th><th className="text-end">Giriş</th><th className="text-end">Çıkış</th><th className="text-end">Kalan bakiye</th><th className="ps-6">Referans</th><th>Aktarım</th>
                                            </tr></thead>
                                            <tbody>
                                                {bakiyeler.length === 0 && <tr><td colSpan={10} className="text-center text-muted py-6">Kayıt yok.</td></tr>}
                                                {bakiyeler.map(b => (
                                                    <tr key={b.id}>
                                                        <td className="text-nowrap">{tarih(b.islemTarihi)} {b.islemSaati}</td>
                                                        <td>{b.hesapNo}</td>
                                                        <td>{b.plaka}</td>
                                                        <td>{b.islemTuruAdi}{b.gecisUcreti && <span className="badge badge-light ms-2">geçiş ücreti</span>}</td>
                                                        <td>{b.aciklama}</td>
                                                        <td className="text-end">{para(b.girisTutari)}</td><td className="text-end">{para(b.cikisTutari)}</td><td className="text-end">{para(b.kalanBakiye)}</td>
                                                        <td className="ps-6">{b.hariciIslemNo}</td><td className="text-nowrap">{tarihSaat(b.aktarimTarihi)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </KTCardBody>
                    </KTCard>

                    <Box flexDirection="row" justifyContent="flex-end" className="mt-6">
                        <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                    </Box>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default HgsKurumsal;
